/**
 * Your web address.
 *
 * TWO ROUTES, ordered by effort. Reserving {name}.{root} needs nothing from
 * anybody. Connecting your own domain is three DNS records and a wait, and it
 * says so before you start rather than after.
 *
 * THE FORM IS ALWAYS HERE. An earlier version of this file replaced the whole
 * panel with "not switched on yet" whenever the platform's Cloudflare settings
 * were missing - so Damon, looking for somewhere to type backups.vtgtx.com,
 * found a sentence instead of a field. Conduit gets this right: it renders both
 * cards unconditionally and lets the server's refusal arrive as an inline
 * error. A screen that hides the control it is named after cannot teach anybody
 * what the product does, and "not configured" is a state of the PLATFORM, not a
 * reason to withhold the page from the tenant.
 *
 * TWO STAGES, NAMED SEPARATELY, and this is lifted from a comment Conduit left
 * after fixing it the hard way. `active` means hostname-active AND ssl-active.
 * Collapsing both into one "waiting on DNS" pill sends somebody to re-check DNS
 * records that were already correct, when the truth was that the certificate
 * was still being issued. Naming the outstanding stage is the difference
 * between "wait a few minutes" and an hour spent re-reading records.
 */

import { useCallback, useEffect, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from './firebase';

interface DnsRecord {
  type: 'CNAME' | 'TXT';
  name: string;
  value: string;
  purpose: 'route' | 'ownership' | 'ssl';
}

interface CustomState {
  host: string;
  status: string;
  sslStatus: string;
  active: boolean;
  records: DnsRecord[];
}

interface WebAddress {
  configured: boolean;
  root: string | null;
  target: string | null;
  subdomain: string | null;
  subdomainHost: string | null;
  custom: CustomState | null;
}

const PURPOSE: Record<DnsRecord['purpose'], string> = {
  route: 'Points the address at us',
  ownership: 'Proves the domain is yours',
  ssl: 'Issues the certificate',
};

function Copy({ text }: { text: string }) {
  const [done, setDone] = useState(false);
  return (
    <button
      className="btn-link"
      onClick={() => {
        void navigator.clipboard.writeText(text).then(() => {
          setDone(true);
          setTimeout(() => setDone(false), 1500);
        });
      }}
    >
      {done ? 'Copied' : 'Copy'}
    </button>
  );
}

/** Live, issuing, or waiting - never a single "pending" that hides which. */
function StatusPill({ s }: { s: CustomState }) {
  if (s.active) return <span className="pill pill-ok">Live</span>;
  if (s.status === 'active') return <span className="pill pill-warn">Issuing certificate</span>;
  return <span className="pill pill-warn">Waiting on DNS</span>;
}

function Step({ done, label, detail }: { done: boolean; label: string; detail?: string }) {
  return (
    <div className="addr-step">
      <span className={done ? 'addr-step-on' : 'addr-step-off'}>{done ? '✓' : '○'}</span>
      <span>{label}{!done && detail ? ` - ${detail}` : ''}</span>
    </div>
  );
}

export function CustomDomainPanel({ orgId, canManage }: { orgId: string | null; canManage: boolean }) {
  const [data, setData] = useState<WebAddress | null>(null);
  const [sub, setSub] = useState('');
  const [avail, setAvail] = useState<{ available: boolean; reason?: string } | null>(null);
  const [host, setHost] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);

  const load = useCallback(async () => {
    if (!orgId) return;
    try {
      const r = await httpsCallable(functions, 'getWebAddress')({ orgId });
      setData(r.data as WebAddress);
    } catch (err) {
      setMsg({ ok: false, text: err instanceof Error ? err.message : 'Could not load your web address.' });
    }
  }, [orgId]);

  useEffect(() => { void load(); }, [load]);

  if (!orgId) return null;

  async function run(key: string, fn: () => Promise<string>) {
    setBusy(key); setMsg(null);
    try {
      setMsg({ ok: true, text: await fn() });
      await load();
    } catch (err) {
      setMsg({ ok: false, text: err instanceof Error ? err.message : 'That did not work.' });
    } finally { setBusy(null); }
  }

  const custom = data?.custom ?? null;
  const root = data?.root ?? null;

  return (
    <section className="panel">
      <div className="panel-title">Your web address</div>
      <p className="panel-note">
        Where your people and your clients find this dashboard, and the name on it. Your name and
        logo appear on that sign-in page, before anybody signs in.
      </p>

      {/* An advisory line, NOT a replacement for the page. Somebody reading this
          still gets to see what the feature is and what it will ask of them. */}
      {data && !data.configured && (
        <div className="banner banner-error">
          Custom web addresses are not switched on for this platform yet, so these will not save.
          A platform administrator adds the Cloudflare token, zone and root domain under
          Integrations in the admin console.
        </div>
      )}

      {msg && <div className={'banner ' + (msg.ok ? 'banner-ok' : 'banner-error')}>{msg.text}</div>}

      {/* ---------------------------------------------------- reserved */}
      <div className="addr-card">
        <div className="addr-title">Reserve an address</div>
        <p className="panel-note" style={{ marginTop: 0 }}>
          Works straight away, with no DNS to set up.
          {root ? <> Yours would be <span className="mono">yourcompany.{root}</span>.</> : null}
        </p>

        {data?.subdomainHost && (
          <div className="addr-live">
            <span className="mono">{data.subdomainHost}</span>
            <span className="pill pill-ok">Reserved</span>
            {canManage && (
              <button
                className="btn-link danger" disabled={busy === 'release'}
                onClick={() => {
                  if (!confirm(`Give up ${data.subdomainHost}?`)) return;
                  void run('release', async () => {
                    await httpsCallable(functions, 'releaseSubdomain')({ orgId });
                    return 'Address released.';
                  });
                }}
              >
                Release
              </button>
            )}
          </div>
        )}

        {canManage && (
          <>
            <div className="addr-row">
              <input
                className="field-input" placeholder="yourcompany"
                value={sub}
                onChange={(e) => { setSub(e.target.value.toLowerCase()); setAvail(null); }}
                onBlur={() => {
                  if (!sub.trim()) return;
                  void (async () => {
                    try {
                      const r = await httpsCallable(functions, 'checkSubdomain')({ orgId, subdomain: sub.trim() });
                      setAvail(r.data as { available: boolean; reason?: string });
                    } catch { setAvail(null); }
                  })();
                }}
              />
              <span className="addr-suffix mono">.{root ?? '...'}</span>
            </div>
            {/* Checked on blur, not per keystroke: the answer for a half-typed
                name is always "available", and flickering yes/no as somebody
                types is noise rather than feedback. */}
            {avail && (
              <p className={avail.available ? 'addr-ok' : 'addr-bad'}>
                {avail.available ? 'That address is free.' : avail.reason}
              </p>
            )}
            <button
              className="btn btn-primary"
              disabled={busy === 'claim' || sub.trim().length < 3 || avail?.available === false}
              onClick={() => void run('claim', async () => {
                await httpsCallable(functions, 'claimSubdomain')({ orgId, subdomain: sub.trim() });
                const s = sub.trim();
                setSub(''); setAvail(null);
                return `${s}.${root} is yours.`;
              })}
            >
              {busy === 'claim' ? 'Reserving...' : data?.subdomainHost ? 'Change address' : 'Reserve address'}
            </button>
          </>
        )}
      </div>

      {/* ---------------------------------------------------- own domain */}
      <div className="addr-card">
        <div className="addr-title">Use your own domain</div>
        <p className="panel-note" style={{ marginTop: 0 }}>
          Prefer <span className="mono">backups.yourcompany.com</span>? Connect it here. We give you
          the DNS records to add at your own provider; it then verifies itself and gets a secure
          certificate automatically. Nothing else is needed from anybody.
        </p>

        {custom ? (
          <>
            <div className="addr-live">
              <span className="mono">{custom.host}</span>
              <StatusPill s={custom} />
              {canManage && (
                <>
                  <button
                    className="btn-link" disabled={busy === 'check'}
                    onClick={() => void run('check', async () => {
                      const r = await httpsCallable(functions, 'checkCustomDomain')({ orgId });
                      const d = r.data as CustomState;
                      if (d.active) return `${d.host} is live and secure.`;
                      return d.status === 'active'
                        ? 'DNS is right. The certificate is still being issued - that is usually a few minutes.'
                        : 'Still waiting on DNS. Changes can take up to an hour to spread.';
                    })}
                  >
                    {busy === 'check' ? 'Checking...' : 'Check now'}
                  </button>
                  <button
                    className="btn-link danger" disabled={busy === 'rm'}
                    onClick={() => {
                      if (!confirm(`Disconnect ${custom.host}?`)) return;
                      void run('rm', async () => {
                        await httpsCallable(functions, 'removeCustomDomain')({ orgId });
                        return 'Domain disconnected.';
                      });
                    }}
                  >
                    Disconnect
                  </button>
                </>
              )}
            </div>

            {!custom.active && (
              <div className="addr-steps">
                <Step done={custom.status === 'active'} label="DNS records found" detail={custom.status} />
                <Step done={custom.sslStatus === 'active'} label="Certificate issued" detail={custom.sslStatus} />
              </div>
            )}

            {!custom.active && custom.records.length > 0 && (
              <>
                <p className="panel-note">
                  Add these wherever DNS for that domain is managed, then press Check now.
                </p>
                <table className="mini-table">
                  <thead><tr><th>Type</th><th>Name</th><th>Value</th><th>Why</th><th></th></tr></thead>
                  <tbody>
                    {custom.records.map((r) => (
                      <tr key={r.purpose + r.name}>
                        <td className="mono">{r.type}</td>
                        <td className="mono addr-wrap">{r.name}</td>
                        <td className="mono addr-wrap">{r.value}</td>
                        <td className="dim">{PURPOSE[r.purpose]}</td>
                        <td><Copy text={r.value} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}
          </>
        ) : canManage ? (
          <div className="addr-row">
            <input
              className="field-input" placeholder="backups.yourcompany.com"
              value={host} onChange={(e) => setHost(e.target.value)}
            />
            <button
              className="btn btn-primary"
              disabled={busy === 'add' || !host.trim()}
              onClick={() => void run('add', async () => {
                await httpsCallable(functions, 'addCustomDomain')({ orgId, host: host.trim() });
                const h = host.trim();
                setHost('');
                return `${h} connected. Add the DNS records below, then press Check now.`;
              })}
            >
              {busy === 'add' ? 'Connecting...' : 'Connect domain'}
            </button>
          </div>
        ) : (
          <p className="empty">No domain connected.</p>
        )}
      </div>
    </section>
  );
}
